import React, { useState } from 'react';
import { X, GripVertical, Music, FileText, FolderOpen, ChevronLeft, ChevronRight, Type, Clock, Play, Volume2, Sparkles, Image, Captions, Layers, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ProjectClip } from './TimelineSequence';

interface SourceFile {
  id: string;
  analysisData?: {
    transcript?: {
      words?: any[];
      segments?: any[];
      text?: string;
      full_text?: string;
    };
  };
}

interface ClipItemProps {
  clip: ProjectClip;
  sourceFile?: SourceFile;  // For looking up transcript when clip's analysisData is stripped
  analysisResults?: {[key: string]: any};  // For looking up transcript from analysis results
  onRemove: () => void;
  onPromptChange: (prompt: string) => void;
  onReorder: (newPosition: number) => void;
  onTimeChange?: (inTime: number | undefined, outTime: number | undefined) => void;
  onOverlayTimeChange?: (clipId: string, inTime: number | undefined, outTime: number | undefined) => void;
  onPreviewClip?: (clipId: string) => void;
  onTitleChange?: (titleText: string | undefined, titlePosition: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | undefined, titleFont?: string, titleColor?: string) => void;
  onVolumeChange?: (volume: number) => void;
  onTransitionChange?: (transitionOut: 'none' | 'fade' | 'fadeSlow' | 'fadeFast' | 'wipeLeft' | 'wipeRight' | 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown') => void;
  onEffectChange?: (effect: 'none' | 'zoomIn' | 'zoomOut' | 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown' | 'zoomInSlow' | 'zoomOutSlow') => void;
  onImageSettingsChange?: (imageMode: 'fullframe' | 'overlay', imagePosition: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | undefined, imageDuration: number, imageScale: number) => void;
  onCaptionsChange?: (showCaptions: boolean, captionStyle: 'default' | 'outline' | 'box' | 'minimal', captionFont?: string, captionBackground?: string, captionFontColor?: string, captionStrokeColor?: string) => void;
  onReferenceTextChange?: (referenceText: string) => void;
  onLayerTypeChange?: (layerType: 'main' | 'background') => void;
  // Multi-track overlay support
  onStackClip?: (draggedClipId: string, targetClipId: string) => void;
  onUnstackClip?: (clipId: string) => void;
  onOverlaySettingsChange?: (clipId: string, position: 'center' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight', scale: number) => void;
  isOnOverlayTrack?: boolean;
  allClips?: ProjectClip[];
  stackedOverlays?: ProjectClip[]; // Overlays stacked on this clip
  position: number;
  totalClips: number;
  renderMode?: 'direct' | 'lambda';
}

export const ClipItem: React.FC<ClipItemProps> = ({
  clip,
  sourceFile,
  analysisResults,
  onRemove,
  onPromptChange,
  onReorder,
  onTimeChange,
  onOverlayTimeChange,
  onPreviewClip,
  onTitleChange,
  onVolumeChange,
  onTransitionChange,
  onEffectChange,
  onImageSettingsChange,
  onCaptionsChange,
  onReferenceTextChange,
  onLayerTypeChange,
  onStackClip,
  onUnstackClip,
  onOverlaySettingsChange,
  isOnOverlayTrack,
  allClips,
  stackedOverlays = [],
  position,
  totalClips,
  renderMode,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [inTime, setInTime] = useState(clip.inTime?.toString() || '');
  const [outTime, setOutTime] = useState(clip.outTime?.toString() || '');
  const [isDragOver, setIsDragOver] = useState<'left' | 'center' | 'right' | null>(null);
  const [expandedOverlayId, setExpandedOverlayId] = useState<string | null>(null);

  // Check if this clip has a linked luma matte mask
  const hasLumaMatte = clip.linkedMaskId !== undefined;

  // Check if this clip is a green screen clip
  const hasChromaKey = clip.chromaKey?.enabled || clip.prompt?.includes('chromaKey') || clip.prompt?.includes('green screen');
  const [titleText, setTitleText] = useState(clip.titleText || '');
  const [titlePosition, setTitlePosition] = useState<'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'>(clip.titlePosition || 'bottom-center');
  const [titleFont, setTitleFont] = useState(clip.titleFont || 'Montserrat');
  const [titleColor, setTitleColor] = useState(clip.titleColor || '#ffffff');

  // Available fonts (same as template builder)
  const FONTS = [
    'Open Sans',
    'Montserrat',
    'Roboto',
    'Poppins',
    'Lato',
    'Inter',
    'Playfair Display',
    'Lora',
    'Oswald',
    'Raleway',
  ];

  // Parse existing chromaKey settings from prompt if available
  const parseChromaKeySettings = () => {
    if (clip.prompt) {
      const thresholdMatch = clip.prompt.match(/threshold:(\d+)/);
      const haloMatch = clip.prompt.match(/halo:(\d+)/);
      return {
        threshold: thresholdMatch ? parseInt(thresholdMatch[1]) : 100,
        halo: haloMatch ? parseInt(haloMatch[1]) : 30
      };
    }
    return { threshold: 100, halo: 30 };
  };
  
  const [chromaKeySettings, setChromaKeySettings] = useState(parseChromaKeySettings());

  // Parse existing volume from prompt if available (for audio clips)
  const parseVolumeFromPrompt = () => {
    if (clip.prompt) {
      const volumeMatch = clip.prompt.match(/volume[:\s]*([\d.]+)/i);
      if (volumeMatch) {
        return parseFloat(volumeMatch[1]);
      }
    }
    return 1.0; // Default to full volume
  };

  const [volume, setVolume] = useState(parseVolumeFromPrompt());

  // Image settings state
  const [imageMode, setImageMode] = useState<'fullframe' | 'overlay'>(clip.imageMode || 'fullframe');
  const [imagePosition, setImagePosition] = useState<'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'>(clip.imagePosition || 'center');
  const [imageDuration, setImageDuration] = useState(clip.imageDuration?.toString() || '5');
  const [imageScale, setImageScale] = useState(clip.imageScale || 0.25); // Default 25% of screen

  // Caption state
  const [showCaptions, setShowCaptions] = useState(clip.showCaptions || false);
  const [captionStyle, setCaptionStyle] = useState<'default' | 'outline' | 'box' | 'minimal'>(clip.captionStyle || 'default');
  const [captionFont, setCaptionFont] = useState(clip.captionFont || 'Open Sans');
  const [captionBackground, setCaptionBackground] = useState(clip.captionBackground || 'dark');
  const [captionFontColor, setCaptionFontColor] = useState(clip.captionFontColor || '#ffffff');
  const [captionStrokeColor, setCaptionStrokeColor] = useState(clip.captionStrokeColor || '#000000');

  // Background options for captions (used with 'box' style)
  const CAPTION_BACKGROUNDS = [
    { value: 'dark', label: 'Dark' },
    { value: 'light', label: 'Light' },
    { value: 'transparent', label: 'None' },
  ];

  // Color presets for captions
  const COLOR_PRESETS = [
    { value: '#ffffff', label: 'White' },
    { value: '#000000', label: 'Black' },
    { value: '#ffff00', label: 'Yellow' },
    { value: '#00ff00', label: 'Green' },
    { value: '#ff0000', label: 'Red' },
    { value: '#00ffff', label: 'Cyan' },
    { value: '#ff00ff', label: 'Magenta' },
    { value: '#ffa500', label: 'Orange' },
  ];

  // Check if clip or source file has transcript data for captions
  // Check for words (preferred for captions), segments (from Whisper), or plain text
  // Also check analysisResults since clip.analysisData may be stripped when saving to DB
  const clipTranscript = clip.analysisData?.transcript;
  const fileTranscript = sourceFile?.analysisData?.transcript;

  // Look up transcript from analysisResults (same pattern as render function)
  const fileAnalysis = sourceFile?.id ? (() => {
    if (!analysisResults) return null;
    const match = Object.values(analysisResults).find((result: any) =>
      result.status === 'completed' &&
      (result.video_id === sourceFile.id || result.results?.video_id === sourceFile.id)
    );
    return match ? ((match as any).results || match) : null;
  })() : null;
  const analysisTranscript = (fileAnalysis as any)?.transcript;

  const hasTranscript = clipTranscript?.words?.length > 0 ||
                        clipTranscript?.segments?.length > 0 ||
                        clipTranscript?.text ||
                        clipTranscript?.full_text ||
                        fileTranscript?.words?.length > 0 ||
                        fileTranscript?.segments?.length > 0 ||
                        fileTranscript?.text ||
                        fileTranscript?.full_text ||
                        analysisTranscript?.words?.length > 0 ||
                        analysisTranscript?.segments?.length > 0 ||
                        analysisTranscript?.text ||
                        analysisTranscript?.full_text;

  // Get transcript text for the clip's time range (for display and editing)
  const getTranscriptForTimeRange = (): string => {
    // If we already have referenceText (user edited), return it
    if (clip.referenceText) return clip.referenceText;

    const inT = clip.inTime || 0;
    const outT = clip.outTime || clip.duration || Infinity;

    // Try to get words or segments from various sources
    const words = clipTranscript?.words || fileTranscript?.words || analysisTranscript?.words;
    const segments = clipTranscript?.segments || fileTranscript?.segments || analysisTranscript?.segments;

    if (words && words.length > 0) {
      // Filter words within the time range
      const filteredWords = words.filter((w: any) => {
        const wordStart = w.start_time ?? w.start ?? 0;
        const wordEnd = w.end_time ?? w.end ?? wordStart;
        return wordStart >= inT && wordEnd <= outT;
      });
      return filteredWords.map((w: any) => w.word || w.text || w.content || '').join(' ').trim();
    }

    if (segments && segments.length > 0) {
      // Filter segments that overlap with the time range
      const filteredSegments = segments.filter((s: any) => {
        const segStart = s.start_time ?? s.start ?? 0;
        const segEnd = s.end_time ?? s.end ?? segStart;
        return segStart < outT && segEnd > inT;
      });
      return filteredSegments.map((s: any) => s.text || '').join(' ').trim();
    }

    // Fall back to full text
    return clipTranscript?.text || clipTranscript?.full_text ||
           fileTranscript?.text || fileTranscript?.full_text ||
           analysisTranscript?.text || analysisTranscript?.full_text || '';
  };

  // State for editable transcript
  const [editableTranscript, setEditableTranscript] = useState(getTranscriptForTimeRange());

  // Drag and drop handlers for stacking clips
  const handleClipDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData('clipId', clip.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleClipDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent parent timeline from handling

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;

    // Determine drop zone: left 25%, center 50%, right 25%
    if (x < width * 0.25) {
      setIsDragOver('left');
    } else if (x > width * 0.75) {
      setIsDragOver('right');
    } else {
      setIsDragOver('center');
    }
    e.dataTransfer.dropEffect = 'move';
  };

  const handleClipDragLeave = () => {
    setIsDragOver(null);
  };

  const handleClipDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent parent timeline from handling

    const draggedClipId = e.dataTransfer.getData('clipId');

    if (!draggedClipId || draggedClipId === clip.id) {
      setIsDragOver(null);
      return;
    }

    // Calculate drop zone at drop time (more reliable than hover state)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    let dropZone: 'left' | 'center' | 'right';
    if (x < width * 0.25) {
      dropZone = 'left';
    } else if (x > width * 0.75) {
      dropZone = 'right';
    } else {
      dropZone = 'center';
    }

    console.log('Drop event - draggedClipId:', draggedClipId, 'targetClipId:', clip.id, 'dropZone:', dropZone, 'x:', x, 'width:', width);

    if (dropZone === 'center' && onStackClip) {
      // Stack the dragged clip on top of this clip
      console.log('Stacking clip', draggedClipId, 'on', clip.id);
      onStackClip(draggedClipId, clip.id);
    } else if (dropZone === 'left') {
      // Reorder: insert before this clip
      onReorder(position);
    } else if (dropZone === 'right') {
      // Reorder: insert after this clip
      onReorder(position + 1);
    }
    setIsDragOver(null);
  };

  // Parse time string in various formats: "4m30s", "4:30", "270", "270s", "4m", etc.
  const parseTimeString = (value: string): number | undefined => {
    if (!value || value.trim() === '') return undefined;

    const trimmed = value.trim().toLowerCase();

    // Format: "4:30" or "4:30.5" (minutes:seconds)
    const colonMatch = trimmed.match(/^(\d+):(\d+(?:\.\d+)?)$/);
    if (colonMatch) {
      const minutes = parseInt(colonMatch[1]);
      const seconds = parseFloat(colonMatch[2]);
      return minutes * 60 + seconds;
    }

    // Format: "4m30s" or "4m30" or "4m" (minutes and optional seconds)
    const mMatch = trimmed.match(/^(\d+)m(?:(\d+(?:\.\d+)?)s?)?$/);
    if (mMatch) {
      const minutes = parseInt(mMatch[1]);
      const seconds = mMatch[2] ? parseFloat(mMatch[2]) : 0;
      return minutes * 60 + seconds;
    }

    // Format: "30s" (just seconds with suffix)
    const sMatch = trimmed.match(/^(\d+(?:\.\d+)?)s$/);
    if (sMatch) {
      return parseFloat(sMatch[1]);
    }

    // Format: plain number (assume seconds)
    const num = parseFloat(trimmed);
    return isNaN(num) ? undefined : num;
  };

  const handleTimeChange = (type: 'in' | 'out', value: string) => {
    const numValue = parseTimeString(value);
    if (type === 'in') {
      setInTime(value);
      if (onTimeChange) {
        onTimeChange(numValue, clip.outTime);
      }
    } else {
      setOutTime(value);
      if (onTimeChange) {
        onTimeChange(clip.inTime, numValue);
      }
    }
  };

  const renderThumbnail = () => {
    // Handle text clips
    if (clip.fileType === 'text') {
      return (
        <div className="w-full h-28 bg-blue-50 border border-blue-200 rounded flex items-center justify-center p-2">
          <div className="text-center">
            <Type className="w-6 h-6 text-blue-600 mx-auto mb-1" />
            <p className="text-xs text-blue-800 font-medium line-clamp-2">
              {clip.prompt || clip.name}
            </p>
          </div>
        </div>
      );
    }
    
    // Handle mask clips - show a special icon
    if (clip.layerType === 'mask' || clip.name?.includes('[Luma Matte]')) {
      return (
        <div className="w-full h-28 bg-gray-100 rounded flex items-center justify-center">
          <div className="text-center">
            <div className="text-4xl mb-1">⚪</div>
            <p className="text-xs text-gray-600 font-medium">Luma Matte</p>
          </div>
        </div>
      );
    }

    if (!clip.thumbnailUrl || clip.thumbnailUrl === 'AUDIO_ICON') {
      return (
        <div className="w-full h-28 bg-muted rounded flex items-center justify-center">
          <Music className="w-8 h-8 text-muted-foreground" />
        </div>
      );
    }

    if (clip.thumbnailUrl === 'SRT_ICON') {
      return (
        <div className="w-full h-28 bg-muted rounded flex items-center justify-center">
          <FileText className="w-8 h-8 text-muted-foreground" />
        </div>
      );
    }

    if (clip.thumbnailUrl.startsWith('blob:') || clip.thumbnailUrl.startsWith('data:') || clip.thumbnailUrl.startsWith('http')) {
      return (
        <div className="w-full h-28 bg-muted rounded overflow-hidden flex items-center justify-center">
          <img
            src={clip.thumbnailUrl}
            alt={clip.name}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      );
    }

    return (
      <div className="w-full h-28 bg-muted rounded flex items-center justify-center">
        <FolderOpen className="w-8 h-8 text-muted-foreground" />
      </div>
    );
  };

  // Get overlay drop zone styling
  const getDropZoneStyles = () => {
    if (!isDragOver) return '';
    if (isDragOver === 'center') {
      return 'ring-4 ring-teal-400 bg-teal-50 dark:bg-teal-950/30';
    }
    if (isDragOver === 'left') {
      return 'border-l-4 border-l-blue-500';
    }
    if (isDragOver === 'right') {
      return 'border-r-4 border-r-blue-500';
    }
    return '';
  };

  // Render mini thumbnail for stacked overlays
  const renderOverlayMiniThumbnail = (overlay: ProjectClip) => {
    if (overlay.thumbnailUrl && overlay.thumbnailUrl.startsWith('blob:') || overlay.thumbnailUrl?.startsWith('data:') || overlay.thumbnailUrl?.startsWith('http')) {
      return (
        <img
          src={overlay.thumbnailUrl}
          alt={overlay.name}
          className="w-full h-full object-cover rounded"
        />
      );
    }
    return (
      <div className="w-full h-full bg-teal-100 dark:bg-teal-900 rounded flex items-center justify-center">
        <Layers className="w-3 h-3 text-teal-600 dark:text-teal-400" />
      </div>
    );
  };

  // Get position label for overlay
  const getPositionLabel = (pos: string | undefined) => {
    const labels: Record<string, string> = {
      center: 'Full',
      topLeft: 'TL',
      topRight: 'TR',
      bottomLeft: 'BL',
      bottomRight: 'BR',
    };
    return labels[pos || 'center'] || 'Full';
  };

  return (
    <div
      className="flex-shrink-0 w-64"
      draggable={renderMode === 'direct'}
      onDragStart={handleClipDragStart}
      onDragOver={handleClipDragOver}
      onDragLeave={handleClipDragLeave}
      onDrop={handleClipDrop}
    >
      <div className={`border border-border rounded-lg bg-background overflow-hidden transition-all ${getDropZoneStyles()} ${isOnOverlayTrack ? 'border-teal-400' : ''}`}>
        {/* Stacked Overlays - compact mini-thumbnails above main clip */}
        {stackedOverlays.length > 0 && renderMode === 'direct' && (
          <div className="bg-teal-50 dark:bg-teal-950/30 border-b border-teal-200 dark:border-teal-800 p-1.5">
            <div className="flex items-center gap-1 mb-1">
              <Layers className="w-3 h-3 text-teal-600 dark:text-teal-400" />
              <span className="text-[10px] font-medium text-teal-700 dark:text-teal-300">
                {stackedOverlays.length} overlay{stackedOverlays.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex gap-1 flex-wrap">
              {stackedOverlays.map((overlay) => (
                <div key={overlay.id} className="relative">
                  {/* Compact overlay thumbnail */}
                  <button
                    onClick={() => setExpandedOverlayId(expandedOverlayId === overlay.id ? null : overlay.id)}
                    className={`w-12 h-8 rounded border-2 overflow-hidden transition-all hover:scale-105 ${
                      expandedOverlayId === overlay.id
                        ? 'border-teal-500 ring-2 ring-teal-300'
                        : 'border-teal-300 dark:border-teal-700'
                    }`}
                    title={`${overlay.name} - Click to ${expandedOverlayId === overlay.id ? 'collapse' : 'expand'}`}
                  >
                    {renderOverlayMiniThumbnail(overlay)}
                  </button>
                  {/* Position indicator */}
                  <span className="absolute -bottom-0.5 -right-0.5 bg-teal-600 text-white text-[8px] px-1 rounded-sm font-medium">
                    {getPositionLabel(overlay.overlayPosition)}
                  </span>
                </div>
              ))}
            </div>

            {/* Expanded overlay controls */}
            {expandedOverlayId && stackedOverlays.find(o => o.id === expandedOverlayId) && (
              <div className="mt-2 p-2 bg-white dark:bg-gray-900 rounded border border-teal-200 dark:border-teal-700 space-y-2">
                {(() => {
                  const overlay = stackedOverlays.find(o => o.id === expandedOverlayId)!;
                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium truncate flex-1" title={overlay.name}>
                          {overlay.name}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-5 w-5 p-0 text-gray-400 hover:text-gray-600"
                          onClick={() => setExpandedOverlayId(null)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>

                      {/* Position select */}
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Position</Label>
                        <Select
                          value={overlay.overlayPosition || 'center'}
                          onValueChange={(value: 'center' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight') => {
                            if (onOverlaySettingsChange) {
                              onOverlaySettingsChange(overlay.id, value, overlay.overlayScale || 0.5);
                            }
                          }}
                        >
                          <SelectTrigger className="h-6 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="center">Center (Full)</SelectItem>
                            <SelectItem value="topLeft">Top Left</SelectItem>
                            <SelectItem value="topRight">Top Right</SelectItem>
                            <SelectItem value="bottomLeft">Bottom Left</SelectItem>
                            <SelectItem value="bottomRight">Bottom Right</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Scale slider - only for non-center */}
                      {overlay.overlayPosition && overlay.overlayPosition !== 'center' && (
                        <div className="space-y-1">
                          <Label className="text-[10px] text-muted-foreground">
                            Size: {Math.round((overlay.overlayScale || 0.5) * 100)}%
                          </Label>
                          <input
                            type="range"
                            min="0.1"
                            max="1.0"
                            step="0.05"
                            value={overlay.overlayScale || 0.5}
                            onChange={(e) => {
                              if (onOverlaySettingsChange) {
                                onOverlaySettingsChange(overlay.id, overlay.overlayPosition || 'center', parseFloat(e.target.value));
                              }
                            }}
                            className="w-full h-1.5 bg-teal-200 rounded-lg appearance-none cursor-pointer"
                          />
                        </div>
                      )}

                      {/* In/Out time for overlay */}
                      {onOverlayTimeChange && (
                        <div className="space-y-1">
                          <Label className="text-[10px] text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Trim
                          </Label>
                          <div className="flex gap-1">
                            <Input
                              type="text"
                              placeholder="In"
                              defaultValue={overlay.inTime?.toString() || ''}
                              onChange={(e) => {
                                const val = parseTimeString(e.target.value);
                                onOverlayTimeChange(overlay.id, val, overlay.outTime);
                              }}
                              className="h-6 text-[10px] flex-1"
                            />
                            <Input
                              type="text"
                              placeholder="Out"
                              defaultValue={overlay.outTime?.toString() || ''}
                              onChange={(e) => {
                                const val = parseTimeString(e.target.value);
                                onOverlayTimeChange(overlay.id, overlay.inTime, val);
                              }}
                              className="h-6 text-[10px] flex-1"
                            />
                          </div>
                          <p className="text-[9px] text-muted-foreground">e.g. 0, 1:30, 2m30s</p>
                        </div>
                      )}

                      {/* Unstack button */}
                      {onUnstackClip && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full h-6 text-[10px] border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => {
                            onUnstackClip(overlay.id);
                            setExpandedOverlayId(null);
                          }}
                        >
                          Remove overlay
                        </Button>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Clip Header */}
        <div className="relative group">
          {/* Thumbnail */}
          {renderThumbnail()}

          {/* Overlay Controls */}
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            {/* Move Left */}
            {position > 0 && (
              <Button
                size="sm"
                variant="secondary"
                className="h-8 w-8 p-0"
                onClick={() => onReorder(position - 1)}
                title="Move left"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}

            {/* Drag Handle */}
            <div className="cursor-move p-2">
              <GripVertical className="h-5 w-5 text-white" />
            </div>

            {/* Move Right */}
            {position < totalClips - 1 && (
              <Button
                size="sm"
                variant="secondary"
                className="h-8 w-8 p-0"
                onClick={() => onReorder(position + 1)}
                title="Move right"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Remove Button */}
          <Button
            size="sm"
            variant="destructive"
            className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={onRemove}
          >
            <X className="h-3 w-3" />
          </Button>

          {/* Position Badge */}
          <div className="absolute top-2 left-2 bg-background/90 backdrop-blur-sm px-2 py-0.5 rounded text-xs font-medium">
            #{position + 1}
          </div>
        </div>

        {/* Clip Info */}
        <div className="p-2 space-y-2">
          <div>
            {/* Layer badges */}
            <div className="flex flex-wrap gap-1 mb-1">
              {clip.layerType && clip.layerType !== 'main' && (
                <span className={`inline-flex items-center px-1 py-0.5 rounded text-xs font-medium ${
                  clip.layerType === 'background' ? 'bg-purple-100 text-purple-700' :
                  clip.layerType === 'audio' ? 'bg-blue-100 text-blue-700' : ''
                }`}>
                  {clip.layerType === 'background' ? '🎨' : '🎵'}
                </span>
              )}
              {clip.isLinked && (
                <span className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                  🔗
                </span>
              )}
              {/* Luma Matte badge - shows when this video has a linked matte */}
              {hasLumaMatte && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300" title="Has linked luma matte for compositing">
                  ⚪ Luma
                </span>
              )}
              {/* Chroma Key badge - shows when clip is green screen */}
              {hasChromaKey && !hasLumaMatte && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300" title="Green screen clip">
                  🟢 Key
                </span>
              )}
              {/* Overlay badge for stacked clips */}
              {clip.isOverlay && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300">
                  Overlay
                </span>
              )}
            </div>
            <p className="text-xs font-medium truncate" title={clip.name}>
              {clip.name}
            </p>
            {clip.duration > 0 && (
              <p className="text-xs text-muted-foreground">
                {Math.round(clip.duration)}s
              </p>
            )}
          </div>

          {/* Editable Transcript - for Quick Render mode with transcript available */}
          {clip.fileType === 'video' && renderMode === 'direct' && hasTranscript && (
            <div className="space-y-1">
              <Label className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <FileText className="w-3 h-3" />
                Transcript {clip.referenceText ? '(edited)' : ''}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground transition-colors">
                      <HelpCircle className="h-3 w-3" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="text-[10px]">Edit to correct transcription errors for captions</p>
                  </TooltipContent>
                </Tooltip>
              </Label>
              <textarea
                className="text-xs w-full bg-muted hover:bg-muted/80 focus:bg-background border border-transparent hover:border-border focus:border-green-500 focus:ring-1 focus:ring-green-500 rounded p-2 resize-none min-h-[3rem]"
                value={editableTranscript}
                onChange={(e) => {
                  setEditableTranscript(e.target.value);
                }}
                onBlur={() => {
                  // Save changes when user clicks away
                  if (onReferenceTextChange && editableTranscript !== clip.referenceText) {
                    onReferenceTextChange(editableTranscript);
                  }
                }}
                rows={2}
                placeholder="Transcript text (edit for captions)"
                title="Edit transcript text - changes will be used for captions"
              />
            </div>
          )}

          {/* Transcript Preview - only show in AI Creative mode (read-only) */}
          {renderMode === 'lambda' && (clip.analysisData?.transcript?.text || clip.referenceText) && (
            <div className="space-y-1">
              <Label className="text-xs text-purple-600 dark:text-purple-400 flex items-center gap-1">
                <FileText className="w-3 h-3" />
                Transcript (AI will read this)
              </Label>
              <div className="text-xs bg-purple-50 dark:bg-purple-950/30 p-2 rounded border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 line-clamp-3">
                {clip.analysisData?.transcript?.text || clip.referenceText}
              </div>
            </div>
          )}

          {/* In/Out Time Fields for video/audio clips */}
          {(clip.fileType === 'video' || clip.fileType === 'audio') && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    In
                  </Label>
                  <Input
                    type="text"
                    value={inTime}
                    onChange={(e) => handleTimeChange('in', e.target.value)}
                    className="h-7 text-xs"
                    placeholder="0 or 1:30"
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Out
                  </Label>
                  <Input
                    type="text"
                    value={outTime}
                    onChange={(e) => handleTimeChange('out', e.target.value)}
                    className="h-7 text-xs"
                    placeholder="2m30s"
                  />
                </div>
              </div>
              {onPreviewClip && (clip.inTime !== undefined || clip.outTime !== undefined) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full h-7 text-xs"
                  onClick={() => onPreviewClip(clip.id)}
                >
                  <Play className="w-3 h-3 mr-1" />
                  Preview Clip
                </Button>
              )}
            </div>
          )}

          {/* Title Overlay for video clips (Quick Render only) */}
          {clip.fileType === 'video' && renderMode === 'direct' && (
            <div className="space-y-2 p-2 bg-purple-50 dark:bg-purple-950/20 rounded">
              <Label className="text-xs font-medium text-purple-700 dark:text-purple-400 flex items-center gap-1">
                <Type className="w-3 h-3" />
                Title Overlay
              </Label>
              <Input
                type="text"
                placeholder="Enter title text..."
                value={titleText}
                onChange={(e) => {
                  const newText = e.target.value;
                  setTitleText(newText);
                  if (onTitleChange) {
                    onTitleChange(newText || undefined, newText ? titlePosition : undefined, newText ? titleFont : undefined, newText ? titleColor : undefined);
                  }
                }}
                className="h-7 text-xs"
              />
              {titleText && (
                <>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Position:</Label>
                    <Select
                      value={titlePosition}
                      onValueChange={(value: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right') => {
                        setTitlePosition(value);
                        if (onTitleChange) {
                          onTitleChange(titleText, value, titleFont, titleColor);
                        }
                      }}
                    >
                      <SelectTrigger className="h-7 text-xs flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="top-left">Top Left</SelectItem>
                        <SelectItem value="top-center">Top Center</SelectItem>
                        <SelectItem value="top-right">Top Right</SelectItem>
                        <SelectItem value="center-left">Center Left</SelectItem>
                        <SelectItem value="center">Center</SelectItem>
                        <SelectItem value="center-right">Center Right</SelectItem>
                        <SelectItem value="bottom-left">Bottom Left</SelectItem>
                        <SelectItem value="bottom-center">Bottom Center</SelectItem>
                        <SelectItem value="bottom-right">Bottom Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Font:</Label>
                    <Select
                      value={titleFont}
                      onValueChange={(value: string) => {
                        setTitleFont(value);
                        if (onTitleChange) {
                          onTitleChange(titleText, titlePosition, value, titleColor);
                        }
                      }}
                    >
                      <SelectTrigger className="h-7 text-xs flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONTS.map((font) => (
                          <SelectItem key={font} value={font}>
                            {font}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Color:</Label>
                    <Select
                      value={titleColor}
                      onValueChange={(value: string) => {
                        setTitleColor(value);
                        if (onTitleChange) {
                          onTitleChange(titleText, titlePosition, titleFont, value);
                        }
                      }}
                    >
                      <SelectTrigger className="h-7 text-xs flex-1">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded border" style={{ backgroundColor: titleColor }} />
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {COLOR_PRESETS.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded border" style={{ backgroundColor: color.value }} />
                              {color.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          )}

          {/* More Options Toggle - for video clips in Quick Render mode */}
          {clip.fileType === 'video' && renderMode === 'direct' && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-7 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setShowMoreOptions(!showMoreOptions)}
            >
              {showMoreOptions ? (
                <>
                  <ChevronUp className="w-3 h-3 mr-1" />
                  Hide Options
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3 mr-1" />
                  More Options
                </>
              )}
            </Button>
          )}

          {/* Volume Control for audio clips - always visible (Quick Render only) */}
          {clip.fileType === 'audio' && renderMode === 'direct' && (
            <div className="space-y-2 p-2 bg-blue-50 dark:bg-blue-950/20 rounded">
              <Label className="text-xs font-medium text-blue-700 dark:text-blue-400 flex items-center gap-1">
                <Volume2 className="w-3 h-3" />
                Volume
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="range"
                  min="0"
                  max="100"
                  value={Math.round(volume * 100)}
                  onChange={(e) => {
                    const newVolume = parseInt(e.target.value) / 100;
                    setVolume(newVolume);
                    if (onVolumeChange) {
                      onVolumeChange(newVolume);
                    }
                  }}
                  className="flex-1 h-6"
                />
                <span className="text-xs w-10 text-right">{Math.round(volume * 100)}%</span>
              </div>
            </div>
          )}

          {/* Collapsible Advanced Options for video clips */}
          {showMoreOptions && (
            <>
            {/* Volume Control for video clips (Quick Render only) */}
          {clip.fileType === 'video' && renderMode === 'direct' && (
            <div className="space-y-2 p-2 bg-blue-50 dark:bg-blue-950/20 rounded">
              <Label className="text-xs font-medium text-blue-700 dark:text-blue-400 flex items-center gap-1">
                <Volume2 className="w-3 h-3" />
                Volume
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="range"
                  min="0"
                  max="100"
                  value={Math.round(volume * 100)}
                  onChange={(e) => {
                    const newVolume = parseInt(e.target.value) / 100;
                    setVolume(newVolume);
                    if (onVolumeChange) {
                      onVolumeChange(newVolume);
                    }
                  }}
                  className="flex-1 h-6"
                />
                <span className="text-xs w-10 text-right">{Math.round(volume * 100)}%</span>
              </div>
            </div>
          )}

          {/* Transition Out for video clips (Quick Render only) */}
          {clip.fileType === 'video' && renderMode === 'direct' && onTransitionChange && (
            <div className="space-y-2 p-2 bg-orange-50 dark:bg-orange-950/20 rounded">
              <Label className="text-xs font-medium text-orange-700 dark:text-orange-400 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Transition Out
              </Label>
              <Select
                value={clip.transitionOut || 'none'}
                onValueChange={(value: 'none' | 'fade' | 'fadeSlow' | 'fadeFast' | 'wipeLeft' | 'wipeRight' | 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown') => {
                  onTransitionChange(value);
                }}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="fade">Fade</SelectItem>
                  <SelectItem value="fadeSlow">Fade (Slow)</SelectItem>
                  <SelectItem value="fadeFast">Fade (Fast)</SelectItem>
                  <SelectItem value="wipeLeft">Wipe Left</SelectItem>
                  <SelectItem value="wipeRight">Wipe Right</SelectItem>
                  <SelectItem value="slideLeft">Slide Left</SelectItem>
                  <SelectItem value="slideRight">Slide Right</SelectItem>
                  <SelectItem value="slideUp">Slide Up</SelectItem>
                  <SelectItem value="slideDown">Slide Down</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Motion Effect for video clips (Ken Burns style zoom/pan) */}
          {clip.fileType === 'video' && renderMode === 'direct' && onEffectChange && (
            <div className="space-y-2 p-2 bg-purple-50 dark:bg-purple-950/20 rounded">
              <Label className="text-xs font-medium text-purple-700 dark:text-purple-400 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Motion Effect
              </Label>
              <Select
                value={clip.effect || 'none'}
                onValueChange={(value: 'none' | 'zoomIn' | 'zoomOut' | 'slideLeft' | 'slideRight' | 'slideUp' | 'slideDown' | 'zoomInSlow' | 'zoomOutSlow') => {
                  onEffectChange(value);
                }}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="zoomIn">Zoom In</SelectItem>
                  <SelectItem value="zoomOut">Zoom Out</SelectItem>
                  <SelectItem value="zoomInSlow">Zoom In (Slow)</SelectItem>
                  <SelectItem value="zoomOutSlow">Zoom Out (Slow)</SelectItem>
                  <SelectItem value="slideLeft">Slide Left</SelectItem>
                  <SelectItem value="slideRight">Slide Right</SelectItem>
                  <SelectItem value="slideUp">Slide Up</SelectItem>
                  <SelectItem value="slideDown">Slide Down</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Captions for video clips with transcript (Quick Render only) */}
          {clip.fileType === 'video' && renderMode === 'direct' && onCaptionsChange && hasTranscript && (
            <div className="space-y-2 p-2 bg-indigo-50 dark:bg-indigo-950/20 rounded">
              <Label className="text-xs font-medium text-indigo-700 dark:text-indigo-400 flex items-center gap-1">
                <Captions className="w-3 h-3" />
                Captions
              </Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`captions-${clip.id}`}
                    checked={showCaptions}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setShowCaptions(newValue);
                      onCaptionsChange(newValue, captionStyle, captionFont, captionBackground, captionFontColor, captionStrokeColor);
                    }}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor={`captions-${clip.id}`} className="text-xs cursor-pointer">
                    Burn-in captions from transcript
                  </Label>
                </div>
                {showCaptions && (
                  <>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-indigo-700 dark:text-indigo-300">Style:</Label>
                      <Select
                        value={captionStyle}
                        onValueChange={(value: 'default' | 'outline' | 'box' | 'minimal') => {
                          setCaptionStyle(value);
                          onCaptionsChange(showCaptions, value, captionFont, captionBackground, captionFontColor, captionStrokeColor);
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Default</SelectItem>
                          <SelectItem value="box">Box</SelectItem>
                          <SelectItem value="minimal">Minimal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-indigo-700 dark:text-indigo-300">Font:</Label>
                      <Select
                        value={captionFont}
                        onValueChange={(value: string) => {
                          setCaptionFont(value);
                          onCaptionsChange(showCaptions, captionStyle, value, captionBackground, captionFontColor, captionStrokeColor);
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FONTS.map((font) => (
                            <SelectItem key={font} value={font}>
                              {font}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {captionStyle === 'box' && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-indigo-700 dark:text-indigo-300">Background:</Label>
                        <Select
                          value={captionBackground}
                          onValueChange={(value: string) => {
                            setCaptionBackground(value);
                            onCaptionsChange(showCaptions, captionStyle, captionFont, value, captionFontColor, captionStrokeColor);
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs flex-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CAPTION_BACKGROUNDS.map((bg) => (
                              <SelectItem key={bg.value} value={bg.value}>
                                {bg.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-indigo-700 dark:text-indigo-300">Text Color:</Label>
                      <Select
                        value={captionFontColor}
                        onValueChange={(value: string) => {
                          setCaptionFontColor(value);
                          onCaptionsChange(showCaptions, captionStyle, captionFont, captionBackground, value, captionStrokeColor);
                        }}
                      >
                        <SelectTrigger className="h-7 text-xs flex-1">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded border" style={{ backgroundColor: captionFontColor }} />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {COLOR_PRESETS.map((color) => (
                            <SelectItem key={color.value} value={color.value}>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded border" style={{ backgroundColor: color.value }} />
                                {color.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {captionStyle !== 'box' && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-indigo-700 dark:text-indigo-300">Stroke:</Label>
                        <Select
                          value={captionStrokeColor}
                          onValueChange={(value: string) => {
                            setCaptionStrokeColor(value);
                            onCaptionsChange(showCaptions, captionStyle, captionFont, captionBackground, captionFontColor, value);
                          }}
                        >
                          <SelectTrigger className="h-7 text-xs flex-1">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded border" style={{ backgroundColor: captionStrokeColor }} />
                              <SelectValue />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            {COLOR_PRESETS.map((color) => (
                              <SelectItem key={color.value} value={color.value}>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded border" style={{ backgroundColor: color.value }} />
                                  {color.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
            </>
          )}

          {/* ChromaKey Settings for green screen clips (but not luma mattes) */}
          {(clip.prompt?.includes('chromaKey') || clip.prompt?.includes('green screen')) &&
           !clip.prompt?.includes('luma matte') && (
            <div className="space-y-2 p-2 bg-green-50 dark:bg-green-950/20 rounded">
              <Label className="text-xs font-medium text-green-700 dark:text-green-400">Green Screen Settings</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs w-20">Threshold:</Label>
                  <Input
                    type="range"
                    min="50"
                    max="150"
                    value={chromaKeySettings.threshold}
                    onChange={(e) => {
                      const newThreshold = parseInt(e.target.value);
                      setChromaKeySettings(prev => ({ ...prev, threshold: newThreshold }));
                      // Always update to the new concise format
                      onPromptChange(`chromaKey #97eca9 threshold:${newThreshold} halo:${chromaKeySettings.halo}`);
                    }}
                    className="flex-1 h-6"
                  />
                  <span className="text-xs w-8">{chromaKeySettings.threshold}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs w-20">Halo:</Label>
                  <Input
                    type="range"
                    min="0"
                    max="100"
                    value={chromaKeySettings.halo}
                    onChange={(e) => {
                      const newHalo = parseInt(e.target.value);
                      setChromaKeySettings(prev => ({ ...prev, halo: newHalo }));
                      onPromptChange(`chromaKey #97eca9 threshold:${chromaKeySettings.threshold} halo:${newHalo}`);
                    }}
                    className="flex-1 h-6"
                  />
                  <span className="text-xs w-8">{chromaKeySettings.halo}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Lower halo = tighter edge, Higher = softer edge
                </p>
              </div>
            </div>
          )}

          {/* Layer Type selector for images and AI-generated videos in Quick Render mode */}
          {(clip.fileType === 'image' || clip.fileType === 'video') && renderMode === 'direct' && onLayerTypeChange && clip.layerType !== 'audio' && clip.layerType !== 'mask' && (
            <div className="space-y-2 p-2 bg-indigo-50 dark:bg-indigo-950/20 rounded border border-indigo-200 dark:border-indigo-800">
              <Label className="text-xs font-medium text-indigo-700 dark:text-indigo-400 flex items-center gap-1">
                <Layers className="w-3 h-3" />
                Layer Type
              </Label>
              <Select
                value={clip.layerType || 'main'}
                onValueChange={(value: 'main' | 'background') => {
                  onLayerTypeChange(value);
                }}
              >
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">Main (foreground clip)</SelectItem>
                  <SelectItem value="background">Background (behind other clips)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-indigo-600 dark:text-indigo-400">
                {clip.layerType === 'background'
                  ? 'This will appear behind all foreground clips'
                  : 'This is a foreground clip in the main timeline'}
              </p>
            </div>
          )}

          {/* Image Settings for image clips in Quick Render mode */}
          {clip.fileType === 'image' && clip.layerType !== 'background' && renderMode === 'direct' && onImageSettingsChange && (
            <div className="space-y-3 p-2 bg-cyan-50 dark:bg-cyan-950/20 rounded border border-cyan-200 dark:border-cyan-800">
              <Label className="text-xs font-medium text-cyan-700 dark:text-cyan-400 flex items-center gap-1">
                <Image className="w-3 h-3" />
                Image Settings
              </Label>

              {/* Mode: Full Frame vs Overlay */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Display Mode</Label>
                <Select
                  value={imageMode}
                  onValueChange={(value: 'fullframe' | 'overlay') => {
                    setImageMode(value);
                    onImageSettingsChange(value, value === 'overlay' ? imagePosition : undefined, parseFloat(imageDuration) || 5, imageScale);
                  }}
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fullframe">Full Frame (timeline clip)</SelectItem>
                    <SelectItem value="overlay">Overlay (on top of video)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Position - only show for overlay mode */}
              {imageMode === 'overlay' && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Position</Label>
                  <Select
                    value={imagePosition}
                    onValueChange={(value: 'top-left' | 'top-center' | 'top-right' | 'center-left' | 'center' | 'center-right' | 'bottom-left' | 'bottom-center' | 'bottom-right') => {
                      setImagePosition(value);
                      onImageSettingsChange(imageMode, value, parseFloat(imageDuration) || 5, imageScale);
                    }}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top-left">Top Left</SelectItem>
                      <SelectItem value="top-center">Top Center</SelectItem>
                      <SelectItem value="top-right">Top Right</SelectItem>
                      <SelectItem value="center-left">Center Left</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                      <SelectItem value="center-right">Center Right</SelectItem>
                      <SelectItem value="bottom-left">Bottom Left</SelectItem>
                      <SelectItem value="bottom-center">Bottom Center</SelectItem>
                      <SelectItem value="bottom-right">Bottom Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Size slider - only show for overlay mode */}
              {imageMode === 'overlay' && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Size: {Math.round(imageScale * 100)}%</Label>
                  <input
                    type="range"
                    min="0.1"
                    max="0.5"
                    step="0.05"
                    value={imageScale}
                    onChange={(e) => {
                      const scale = parseFloat(e.target.value);
                      setImageScale(scale);
                      onImageSettingsChange(imageMode, imagePosition, parseFloat(imageDuration) || 5, scale);
                    }}
                    className="w-full h-2 bg-cyan-200 rounded-lg appearance-none cursor-pointer dark:bg-cyan-700"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>10%</span>
                    <span>50%</span>
                  </div>
                </div>
              )}

              {/* Duration */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Duration (seconds)</Label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  step="0.5"
                  value={imageDuration}
                  onChange={(e) => {
                    setImageDuration(e.target.value);
                    const dur = parseFloat(e.target.value) || 5;
                    onImageSettingsChange(imageMode, imageMode === 'overlay' ? imagePosition : undefined, dur, imageScale);
                  }}
                  className="h-7 text-xs"
                  placeholder="5"
                />
              </div>

              <p className="text-xs text-cyan-600 dark:text-cyan-400">
                {imageMode === 'fullframe'
                  ? 'Image will appear as a standalone clip in the timeline'
                  : `Image will overlay at ${Math.round(imageScale * 100)}% size in the ${imagePosition.replace('-', ' ')}`}
              </p>
            </div>
          )}

          {/* Clip-specific AI instructions (collapsed by default, for AI Creative mode only) */}
          {renderMode === 'lambda' && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                + Add instructions for this clip
              </summary>
              <div className="space-y-1 mt-2">
                <Textarea
                  placeholder="e.g., Use as overlay on previous video, add title 'Summer Sale', fade in, slow motion, text at bottom..."
                  value={clip.prompt}
                  onChange={(e) => onPromptChange(e.target.value)}
                  className="min-h-[60px] text-xs resize-none"
                />
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};
